import { createFixedDateEventKey, createLastMomentSendEventKey } from "../../../utils/events";
import { type ISO8601_TIME } from "../../../utils/iso8601";
import { uuidv4, type UUID } from "../../../utils/uuid";
import type { EmailMoment } from "../../email-moment/types";
import type { HandleSaveMoment } from "../../moment/component/buttons/hooks/types";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import type { FollowUpMomentDetailsLoaderData } from "../hooks/use-follow-up-moment-details";
import type { FollowUpSchemaType } from "../reducer/schema";

const STATIC_PROPERTIES = {
  description: null,
  discover_moment_launched_from: null,
  notify_channel: false, // Follow-up moments not allowed for notify channel moments
  poll_ids: null, // Polls not enabled for follow ups
  source_moment_id: null,
  target_channel: false,
  teams_channel_message_notify: false,
  thread_launched_from: null,
  user_prompt: null,
  chat_moment: null,
  segment: null,
  additional_recipients: [],
  only_additional_recipients: false,
  cc_bcc: null,
  cover_image_path: null,
  whatsapp_moment_id: null,
  whatsapp_template_id: null,
  whatsapp_moment: null,
};

export function followUpToHandleSaveMoment(
  ui: FollowUpSchemaType,
  momentDetailsData: FollowUpMomentDetailsLoaderData | null,
  saveData: HandleSaveMoment | null,
  useLexicalEditor: boolean,
  emailMoment: EmailMoment | null,
  dynamicSegmentIdMap: Record<string, string> | null,
): HandleSaveMoment | null {
  if (!momentDetailsData) return null;

  return {
    ...STATIC_PROPERTIES,
    use_lexical_editor: useLexicalEditor,
    email_moment: useLexicalEditor ? null : syncEmailMomentWithChannelData(emailMoment, ui),
    dynamic_segment_id_map: useLexicalEditor ? null : dynamicSegmentIdMap,
    id: saveData?.id ?? momentDetailsData.followUp?.moment_id ?? uuidv4(),
    follow_up: {
      id: saveData?.follow_up?.id ?? momentDetailsData.followUp?.id ?? uuidv4(),
      config: ui.reason,
    },
    unrecoverable_error: false,
    program_id: momentDetailsData.momentFollowUpDetails.program_id,
    title: ui.title,
    design: ui.design,
    target_manager: momentDetailsData.momentFollowUpDetails.target_manager ?? false,
    target_connection_type: momentDetailsData.momentFollowUpDetails.target_connection_type ?? null,
    channel: ui.channel,
    ...channelHandler(ui),
    ...scheduleHandler(
      ui,
      momentDetailsData.momentFollowUpDetails.id,
      saveData?.schedule?.id ?? momentDetailsData.followUp?.schedule?.id ?? uuidv4(),
    ),
  };
}

/** Sync email_moment.subject and from_address from the follow-up's channelData,
 *  since the user edits these via channelData but email_moment stores its own copies. */
function syncEmailMomentWithChannelData(
  emailMoment: EmailMoment | null,
  ui: FollowUpSchemaType,
): EmailMoment | null {
  if (!emailMoment) return null;
  if (ui.channel !== "work_email" && ui.channel !== "personal_email") return emailMoment;
  return {
    ...emailMoment,
    subject: ui.channelData.email_subject,
    from_address: ui.channelData.email_sender,
  };
}

function channelHandler(
  followUp: FollowUpSchemaType,
): Pick<MomentFormMoment, "from" | "email_subject" | "email_design_id"> {
  switch (followUp.channel) {
    case "slack":
      return {
        from: followUp.channelData.slack_alias_id ?? null,
        email_subject: null,
        email_design_id: null,
      };
    case "work_email":
    case "personal_email":
      return {
        from: followUp.channelData.email_sender,
        email_subject: followUp.channelData.email_subject,
        email_design_id: followUp.channelData.email_design_id as UUID,
      };
    case "teams":
    case "twilio":
    case "whatsapp":
      return {
        from: null,
        email_subject: null,
        email_design_id: null,
      };
  }
}

function scheduleHandler(
  followUp: FollowUpSchemaType,
  momentId: UUID,
  existingScheduleId: UUID,
): Pick<MomentFormMoment, "immediate" | "schedule" | "timezone"> {
  switch (followUp.schedule.type) {
    case "immediate":
      return {
        immediate: true,
        schedule: null,
        timezone: null,
      };
    case "specific":
      return {
        immediate: false,
        timezone: followUp.schedule.timezone,
        schedule: {
          id: existingScheduleId,
          dow: {
            monday: 1,
            tuesday: 1,
            wednesday: 1,
            thursday: 1,
            friday: 1,
            saturday: 1,
            sunday: 1,
          },
          event_initial: true,
          event_repeat: false,
          send_at: (followUp.schedule.time ?? "12:00") as ISO8601_TIME,
          interval_json: null,
          on_event_key: null,
          event: followUp.schedule.date
            ? {
                key: createFixedDateEventKey(),
                title: "",
                calendar: true,
                date: followUp.schedule.date,
                library: false,
                repeat: null,
                number_of_repeats: 0,
                allow_initial: true,
                allow_repeats: false,
                has_schedule_event: false,
              }
            : null,
        },
      };
    case "scheduled":
      return {
        immediate: false,
        timezone: followUp.schedule.timezone,
        schedule: {
          id: existingScheduleId,
          dow: {
            monday: 1,
            tuesday: 1,
            wednesday: 1,
            thursday: 1,
            friday: 1,
            saturday: followUp.schedule.allow_weekends ? 1 : 0,
            sunday: followUp.schedule.allow_weekends ? 1 : 0,
          },
          event_initial: true,
          event_repeat: false,
          send_at: followUp.schedule.time ?? ("12:00" as ISO8601_TIME),
          interval_json:
            followUp.schedule.period && followUp.schedule.amount
              ? { [followUp.schedule.period]: followUp.schedule.amount }
              : {},
          on_event_key: followUp.schedule.allow_weekends
            ? ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]
            : ["monday", "tuesday", "wednesday", "thursday", "friday"],
          event: {
            key: createLastMomentSendEventKey(momentId),
            title: "",
            calendar: false,
            date: undefined,
            library: false,
            repeat: null,
            number_of_repeats: 0,
            allow_initial: true,
            allow_repeats: false,
            has_schedule_event: false,
          },
        },
      };
  }
}
